import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import { required, Validators } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeChildTreatmentQuestionnaire } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';
import { FieldSelectList } from '../../../../../components/redux-form/fields/FieldSelectList';
import { FieldTextArea } from '../../../../../components/redux-form/fields/FieldTextArea';

export interface ChildTreatmentQuestionnaireFormData {
    student: string;
    formId: string;
    patientName: string;
    patientDob: string;
    patientAge: string;
    patientAddress: string;
    patientGender: string;
    patientGenderOther: string;
    patientSexAssigned: string;
    patientSexOther: string;
    patientPronouns: string;
    patientPronounsOther: string;
    patientEthnicity: string;
    patientCurrentSchool: string;
    patientGradeLevel: string;

    patientIsSpecialEducation: boolean;
    patientSpecialEducation: string;

    patientEmergencyContactName: string;
    patientEmergencyContactPhone: string;

    patientReferredByName: string;
    patientReferredBySpecialty: string;

    guardianFirstName: string;
    guardianFirstPhone: string;
    guardianFirstEmail: string;
    guardianSecondName: string;
    guardianSecondPhone: string;
    guardianSecondEmail: string;

    primaryConcern: string;
    isReceiveCurrentTreatment: string;
    currentSpecialists: [];
    currentSpecialistsOther: string;
    currentSymptoms: [];
    explainOtherSymptom: string;

    additionalInfoCurrentConcern: string;

    isPriorPsychiatric: boolean;
    priorPsychiatricInfo: string;

    isPriorDiagnosis: boolean;
    priorDiagnosisInfo: string;

    isPriorTreatment: boolean;
    priorTreatmentInfo: string;

    anyMedicalInfo: string;
    majorSurgeries: string;
    beforeMedicalCondition: string;
    currentMedicalCondition: string;
    previousMedicationInfo: string;
    currentlyMedicationInfo: string;

    whoLivesInHome: string;
    whereGrowUp: string;
    languageInHome: string;
    primaryCaregivers: string;
    guardianFirst: string;
    guardianSecond: string;
    childcarePrograms: string;
    marriedParents: string;
    whenSeparated: string;
    remarryParents: string;
    ageWhenRemarry: string;
    parentDiagnosis: string;

    isBadHabit: boolean;
    badHabits: [];
    badHabitAdditionalInfo: string;

    activitiesEnjoy: string;
    anyAdditionalInfo: string;
}

interface ChildTreatmentQuestionnaireFormProps {
    initialValues: any;
    setIsLoader: any;
    isSpecialEducation: any;
    setIsSpecialEducation: any;
    isReceiveCurrentTreatment: any;
    setIsReceiveCurrentTreatment: any;
    isPriorPsychiatricShow: any;
    setIsPriorPsychiatricShow: any;
    isPriorDiagnosisShow: any;
    setIsPriorDiagnosisShow: any;
    isPriorTreatmentShow: any;
    setIsPriorTreatmentShow: any;
    isBadHabitShow: any;
    setIsBadHabitShow: any;
}

type InjectedProps = InjectedFormProps<ChildTreatmentQuestionnaireFormData, ChildTreatmentQuestionnaireFormProps>;

const validators: Validators<ChildTreatmentQuestionnaireFormData> = {
    patientName: [required({ message: "It's required." })],
    patientDob: [required({ message: "It's required." })],
    patientAge: [required({ message: "It's required." })],
    patientAddress: [required({ message: "It's required." })],
    patientIsSpecialEducation: [required({ message: "It's required." })],
    patientEmergencyContactName: [required({ message: "It's required." })],
    patientEmergencyContactPhone: [required({ message: "It's required." })],
    primaryConcern: [required({ message: "It's required." })],
    isReceiveCurrentTreatment: [required({ message: "It's required." })],
    currentSymptoms: [required({ message: "It's required." })],
    isBadHabit: [required({ message: "It's required." })],
};

const ChildTreatmentQuestionnaireForm: React.FunctionComponent<
    InjectedProps & ChildTreatmentQuestionnaireFormProps
> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
    setIsSpecialEducation,
    isSpecialEducation,
    setIsReceiveCurrentTreatment,
    isReceiveCurrentTreatment,
    setIsPriorPsychiatricShow,
    isPriorPsychiatricShow,
    setIsPriorDiagnosisShow,
    isPriorDiagnosisShow,
    setIsPriorTreatmentShow,
    isPriorTreatmentShow,
    setIsBadHabitShow,
    isBadHabitShow,
}: InjectedProps & ChildTreatmentQuestionnaireFormProps) => {
    const currentSpecialistsValues: SelectObject[] = [
        { label: 'Inpatient Treatment', value: 1 },
        { label: 'Therapist (outpatient mental health professional)', value: 2 },
        { label: 'Psychiatrist', value: 3 },
        { label: 'Developmental Pediatrician', value: 4 },
        { label: 'Neurologist', value: 5 },
        { label: 'School Counselor', value: 6 },
        { label: 'Other', value: 7 },
    ];

    const currentSymptomsValues: SelectObject[] = [
        { label: 'Feeling sad or down, extreme feelings of guilt or crying spells', value: 1 },
        {
            label: 'Confused thinking, reduced ability to concentrate, difficulty starting or completing tasks',
            value: 2,
        },
        { label: 'Excessive energy, hyperactivity, impulsivity or racing thoughts', value: 3 },
        { label: 'Excessive fears or worries or panic attacks', value: 4 },
        { label: 'Extreme mood changes or irritability', value: 5 },
        { label: 'Loss of interests or withdrawal from friends and activities', value: 6 },
        { label: 'Significant tiredness, low energy, fatigue or problems sleeping', value: 7 },
        { label: 'Detachment from reality, paranoia, suspiciousness or hallucinations', value: 8 },
        { label: 'Inability to cope with daily problems or stress', value: 9 },
        { label: 'Trouble understanding and relating to situations and to people', value: 10 },
        { label: 'Risky activity or substance (alcohol or drug) use', value: 11 },
        { label: 'Major changes in eating habits or appetite', value: 12 },
        { label: 'Excessive anger, hostility or violence', value: 13 },
        { label: 'Suicidal thinking, self-harm, intrusive thoughts of death or dying', value: 14 },
        { label: 'Other', value: 15 },
    ];

    const badHabitsValues: SelectObject[] = [
        { label: 'Alcohol', value: 1 },
        { label: 'Tobacco', value: 2 },
        { label: 'Marijuana', value: 3 },
        { label: 'Illegal Drugs', value: 4 },
        { label: 'Prescription Drugs (not prescribed)', value: 5 },
        { label: 'Sexual Activity (not including intercourse)', value: 6 },
        { label: 'Sexual Intercourse', value: 7 },
    ];

    const patientIsSpecialEducationValues: SelectObject[] = [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 2 },
    ];

    const isReceiveCurrentTreatmentValues: SelectObject[] = [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 2 },
    ];

    const priorPsychiatricValues: SelectObject[] = [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 2 },
    ];
    const priorDiagnosisValues: SelectObject[] = [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 2 },
    ];
    const priorTreatmentValues: SelectObject[] = [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 2 },
    ];

    const isBadHabitsValues: SelectObject[] = [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 2 },
    ];

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Mental Health Intake Questionnaire: Child/Adolescent</h2>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={2}
                wrapperConfig={{
                    label: '',
                }}
            />

            <h3 className={classNames(styles.subTitle)}>Child/Adolescent Questionnaire</h3>
            <FieldInput
                name="patientName"
                type="text"
                validate={validators.patientName}
                wrapperConfig={{
                    label: 'Patient name: *',
                }}
            />
            <FieldDate
                name="patientDob"
                type="text"
                date={true}
                validate={validators.patientDob}
                wrapperConfig={{
                    label: 'Date of Birth: *',
                }}
            />
            <FieldInput
                name="patientAge"
                type="text"
                validate={validators.patientAge}
                wrapperConfig={{
                    label: 'Age: *',
                }}
            />
            <FieldInput
                name="patientAddress"
                type="text"
                validate={validators.patientAddress}
                wrapperConfig={{
                    label: 'Address: *',
                }}
            />
            <FieldInput
                name="patientGender"
                type="text"
                validate={validators.patientGender}
                wrapperConfig={{
                    label: "Which most closely describes the patient's gender?:",
                }}
            />
            <FieldTextArea
                name="patientGenderOther"
                validate={validators.patientGenderOther}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Specify other:',
                }}
            />
            <FieldInput
                name="patientSexAssigned"
                type="text"
                validate={validators.patientSexAssigned}
                wrapperConfig={{
                    label: 'Sex assigned at birth:',
                }}
            />
            <FieldTextArea
                name="patientSexOther"
                validate={validators.patientSexOther}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'If other, please specify:',
                }}
            />

            <FieldInput
                name="patientPronouns"
                type="text"
                validate={validators.patientPronouns}
                wrapperConfig={{
                    label: 'Pronouns:',
                }}
            />

            <FieldTextArea
                name="patientPronounsOther"
                validate={validators.patientPronounsOther}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Specify other:',
                }}
            />

            <FieldInput
                name="patientEthnicity"
                type="text"
                validate={validators.patientEthnicity}
                wrapperConfig={{
                    label: 'Race/Ethnicity:',
                }}
            />

            <FieldInput
                name="patientCurrentSchool"
                type="text"
                validate={validators.patientCurrentSchool}
                wrapperConfig={{
                    label: 'Current school:',
                }}
            />

            <FieldInput
                name="patientGradeLevel"
                type="text"
                validate={validators.patientGradeLevel}
                wrapperConfig={{
                    label: 'Current grade level:',
                }}
            />

            <label htmlFor="patientIsSpecialEducation">Does the patient current receive special education services?: *</label>
            <FieldSelectList
                multiple={false}
                name="patientIsSpecialEducation"
                data={patientIsSpecialEducationValues}
                validate={validators.patientIsSpecialEducation}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        if (e?.label == 'Yes') {
                            setIsSpecialEducation(true);
                        } else {
                            setIsSpecialEducation(false);
                        }
                    }
                }}
            />

            {isSpecialEducation ? (
                <FieldTextArea
                    name="patientSpecialEducation"
                    validate={validators.patientSpecialEducation}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Please, specify which special education services:',
                    }}
                />
            ) : (
                <FieldInput
                    name="patientSpecialEducation"
                    type="hidden"
                    validate={validators.patientSpecialEducation}
                    wrapperConfig={{
                        label: '',
                    }}
                />
            )}

            <FieldInput
                name="patientEmergencyContactName"
                type="text"
                validate={validators.patientEmergencyContactName}
                wrapperConfig={{
                    label: 'Emergency contact name/relationship: *',
                }}
            />
            <FieldInput
                name="patientEmergencyContactPhone"
                type="text"
                validate={validators.patientEmergencyContactPhone}
                wrapperConfig={{
                    label: "Emergency contact's phone number: *",
                }}
            />

            <FieldInput
                name="patientReferredByName"
                type="text"
                validate={validators.patientReferredByName}
                wrapperConfig={{
                    label: 'Referred by name:',
                }}
            />

            <FieldInput
                name="patientReferredBySpecialty"
                type="text"
                validate={validators.patientReferredBySpecialty}
                wrapperConfig={{
                    label: 'Referred by specialty:',
                }}
            />

            <h3 className={classNames(styles.subTitle)}>Less than 18 years old</h3>
            <div className={classNames(styles.fixNotice)}>
                If Patient is less that 18 years old, please provide the parents' or legal guardians' information below
            </div>

            <FieldInput
                name="guardianFirstName"
                type="text"
                validate={validators.guardianFirstName}
                wrapperConfig={{
                    label: 'Guardian 1 name:',
                }}
            />
            <FieldInput
                name="guardianFirstPhone"
                type="text"
                validate={validators.guardianFirstPhone}
                wrapperConfig={{
                    label: 'Guardian 1 phone number(s):',
                }}
            />
            <FieldInput
                name="guardianFirstEmail"
                type="text"
                validate={validators.guardianFirstEmail}
                wrapperConfig={{
                    label: 'Guardian 1 email address:',
                }}
            />

            <FieldInput
                name="guardianSecondName"
                type="text"
                validate={validators.guardianSecondName}
                wrapperConfig={{
                    label: 'Guardian 2 name:',
                }}
            />
            <FieldInput
                name="guardianSecondPhone"
                type="text"
                validate={validators.guardianSecondPhone}
                wrapperConfig={{
                    label: 'Guardian 2 phone number(s):',
                }}
            />
            <FieldInput
                name="guardianSecondEmail"
                type="text"
                validate={validators.guardianSecondEmail}
                wrapperConfig={{
                    label: 'Guardian 2 email address:',
                }}
            />

            <h3 className={classNames(styles.subTitle)}>Psychological Concerns and Treatment</h3>
            <div className={classNames(styles.fixNotice)}>Current Concerns</div>
            <FieldInput
                name="primaryConcern"
                type="text"
                validate={validators.primaryConcern}
                wrapperConfig={{
                    label: 'What is the primary concern?: *',
                }}
            />

            <label htmlFor="isReceiveCurrentTreatment">
                Is the patient currently receiving, or have they received, treatment for this concern within the past
                year?:
            </label>
            <FieldSelectList
                multiple={false}
                name="isReceiveCurrentTreatment"
                data={isReceiveCurrentTreatmentValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        if (e?.label == 'Yes') {
                            setIsReceiveCurrentTreatment(true);
                        } else {
                            setIsReceiveCurrentTreatment(false);
                        }
                    }
                }}
            />

            {isReceiveCurrentTreatment ? (
                <div>
                    <label htmlFor="currentSpecialists">Please check all other professionals seen for this concern within the past year:</label>
                    <FieldSelectList
                        multiple={true}
                        name="currentSpecialists"
                        data={currentSpecialistsValues}
                        textField={'label'}
                        valueField={'value'}
                        onChange={(e: any) => {
                            if (e) {
                                const other = e.filter((it: { label: string; value: number }) =>
                                    it.label == 'Other' ? it.value : 0,
                                );
                                if (other && other[0] !== undefined) {
                                    // setIsOtherCommunication(true);
                                } else {
                                    // setIsOtherCommunication(false);
                                }
                            }
                        }}
                    />
                    <FieldInput
                        name="currentSpecialistsOther"
                        type="text"
                        validate={validators.currentSpecialistsOther}
                        wrapperConfig={{
                            label: 'If other, please list the type of professional seen:',
                        }}
                    />
                    <label htmlFor="currentSymptoms">Current Symptoms (Check all that apply):</label>
                    <FieldSelectList
                        multiple={true}
                        name="currentSymptoms"
                        data={currentSymptomsValues}
                        textField={'label'}
                        valueField={'value'}
                        onChange={(e: any) => {}}
                    />
                    <FieldInput
                        name="explainOtherSymptom"
                        type="text"
                        validate={validators.explainOtherSymptom}
                        wrapperConfig={{
                            label: 'Please explain other:',
                        }}
                    />
                </div>
            ) : (
                <div></div>
            )}

            <FieldInput
                name="additionalInfoCurrentConcern"
                type="text"
                validate={validators.additionalInfoCurrentConcern}
                wrapperConfig={{
                    label: 'Please provide additional information about your current concern:',
                }}
            />

            <h3 className={classNames(styles.subTitle)}>History of Psychological Treatment</h3>

            <label htmlFor="isPriorPsychiatric">Prior Psychiatric Evaluations?:</label>
            <FieldSelectList
                multiple={false}
                name="isPriorPsychiatric"
                data={priorPsychiatricValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        if (e?.label == 'Yes') {
                            setIsPriorPsychiatricShow(true);
                        } else {
                            setIsPriorPsychiatricShow(false);
                        }
                    }
                }}
            />

            {isPriorPsychiatricShow ? (
                <FieldTextArea
                    name="priorPsychiatricInfo"
                    validate={validators.priorPsychiatricInfo}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label:
                            'If yes, please provide the type/s of evaluation/s, the evaluator/agency, and date of evaluation/s:',
                    }}
                />
            ) : (
                <FieldInput
                    name="priorPsychiatricInfo"
                    type="hidden"
                    validate={validators.priorPsychiatricInfo}
                    wrapperConfig={{
                        label: '',
                    }}
                />
            )}

            <label htmlFor="isPriorDiagnosis">Prior Psychological Diagnosis(es):</label>
            <FieldSelectList
                multiple={false}
                name="isPriorDiagnosis"
                data={priorDiagnosisValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        if (e?.label == 'Yes') {
                            setIsPriorDiagnosisShow(true);
                        } else {
                            setIsPriorDiagnosisShow(false);
                        }
                    }
                }}
            />

            {isPriorDiagnosisShow ? (
                <FieldTextArea
                    name="priorDiagnosisInfo"
                    validate={validators.priorDiagnosisInfo}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label:
                            'If yes, please provide the diagnosis/es, professional(s) that diagnosed, and the date(s) of diagnosis/es:',
                    }}
                />
            ) : (
                <FieldInput
                    name="priorDiagnosisInfo"
                    type="hidden"
                    validate={validators.priorDiagnosisInfo}
                    wrapperConfig={{
                        label: '',
                    }}
                />
            )}

            <label htmlFor="isPriorTreatment">Prior Psychiatric Treatment?:</label>
            <FieldSelectList
                multiple={false}
                name="isPriorTreatment"
                data={priorTreatmentValues}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        if (e?.label == 'Yes') {
                            setIsPriorTreatmentShow(true);
                        } else {
                            setIsPriorTreatmentShow(false);
                        }
                    }
                }}
            />

            {isPriorTreatmentShow ? (
                <FieldTextArea
                    name="priorTreatmentInfo"
                    validate={validators.priorTreatmentInfo}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label:
                            'If yes, please provide the name of the treatment provider/agency, dates of treatment, and description of treatment:',
                    }}
                />
            ) : (
                <FieldInput
                    name="priorTreatmentInfo"
                    type="hidden"
                    validate={validators.priorTreatmentInfo}
                    wrapperConfig={{
                        label: '',
                    }}
                />
            )}

            <h3 className={classNames(styles.subTitle)}>Medical History</h3>

            <FieldTextArea
                name="currentlyMedicationInfo"
                validate={validators.currentlyMedicationInfo}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        'What medication(s) is the patient CURRENTLY prescribed (please include the name(s) of the medication(s), the dose(s), name(s) of prescriber(s), the date(s) the patient started it, and the reason for taking it)?:',
                }}
            />

            <FieldTextArea
                name="previousMedicationInfo"
                validate={validators.previousMedicationInfo}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        'What medications have been prescribed in the past year that are NO LONGER taken (please include the name(s) of the medication(s), the dose(s), name(s) of prescriber(s), the date(s) the patient started it, and the reason for taking it)?:',
                }}
            />

            <FieldTextArea
                name="currentMedicalCondition"
                validate={validators.currentMedicalCondition}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Please list any medical conditions that the patient is CURRENTLY being treated for:',
                }}
            />

            <FieldTextArea
                name="beforeMedicalCondition"
                validate={validators.beforeMedicalCondition}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Please list previous medical conditions that the patient is NO LONGER being treated for:',
                }}
            />

            <FieldTextArea
                name="majorSurgeries"
                validate={validators.majorSurgeries}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Please list any major surgeries (prior and pending):',
                }}
            />

            <FieldTextArea
                name="anyMedicalInfo"
                validate={validators.anyMedicalInfo}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Any other significant medical history?:',
                }}
            />

            <h3 className={classNames(styles.subTitle)}>Family Structure and History</h3>

            <FieldTextArea
                name="whoLivesInHome"
                validate={validators.whoLivesInHome}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Who lives in the home (please include names, ages, and relation to patient)?:',
                }}
            />

            <FieldTextArea
                name="whereGrowUp"
                validate={validators.whereGrowUp}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Where did the patient grow up (city, state, and years residing in each)?:',
                }}
            />

            <FieldTextArea
                name="languageInHome"
                validate={validators.languageInHome}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Languages spoken in the home:',
                }}
            />

            <FieldTextArea
                name="primaryCaregivers"
                validate={validators.primaryCaregivers}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: "Who are the patient's primary caregivers?:",
                }}
            />

            <FieldTextArea
                name="guardianFirst"
                validate={validators.guardianFirst}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Guardian 1:',
                }}
            />
            <FieldTextArea
                name="guardianSecond"
                validate={validators.guardianSecond}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Guardian 2:',
                }}
            />
            <FieldTextArea
                name="childcarePrograms"
                validate={validators.childcarePrograms}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Childcare and afterschool programs:',
                }}
            />
            <FieldTextArea
                name="marriedParents"
                validate={validators.marriedParents}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: "Are the patient's parents still married?:",
                }}
            />
            <FieldTextArea
                name="whenSeparated"
                validate={validators.whenSeparated}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'If no, how old was the patient when they separated?:',
                }}
            />
            <FieldTextArea
                name="remarryParents"
                validate={validators.remarryParents}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: "If no, did the patient's parents remarry?:",
                }}
            />
            <FieldTextArea
                name="ageWhenRemarry"
                validate={validators.ageWhenRemarry}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'If yes, which parent(s), and how old was the patient when their parent(s) remarried?:',
                }}
            />
            <FieldTextArea
                name="parentDiagnosis"
                validate={validators.parentDiagnosis}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        'Family history of psychiatric difficulties; list relation to patient and diagnosis (i.e. brother- anxiety):',
                }}
            />

            <h3 className={classNames(styles.subTitle)}>Substance Use History</h3>

            <label htmlFor="isBadHabit">
                To your knowledge, has the patient ever tried the following: Alcohol, Tobacco, Marijuana, Illegal Drugs,
                Prescription Drugs (not prescribed), Sexual Activity (not including intercourse), Sexual Intercourse:
            </label>
            <FieldSelectList
                multiple={false}
                name="isBadHabit"
                data={isBadHabitsValues}
                validate={validators.isBadHabit}
                textField={'label'}
                valueField={'value'}
                onChange={(e: any) => {
                    if (e) {
                        if (e?.label == 'Yes') {
                            setIsBadHabitShow(true);
                        } else {
                            setIsBadHabitShow(false);
                        }
                    }
                }}
            />

            {isBadHabitShow ? (
                <div>
                    <label htmlFor="badHabits">Please check all that apply:</label>
                    <FieldSelectList
                        multiple={true}
                        name="badHabits"
                        data={badHabitsValues}
                        textField={'label'}
                        valueField={'value'}
                    />
                    <FieldTextArea
                        name="badHabitAdditionalInfo"
                        validate={validators.badHabitAdditionalInfo}
                        wrapperConfig={{
                            classes: {
                                label: 'fullSize',
                            },
                            label:
                                'If yes to any of the above, please provide additional information including dates and frequency:',
                        }}
                    />
                </div>
            ) : null}

            <h3 className={classNames(styles.subTitle)}>Additional Information</h3>
            <div className={classNames(styles.fixNotice)}>
                Please feel free to provide any additional information that you would like the therapist to know
            </div>

            <FieldTextArea
                name="activitiesEnjoy"
                validate={validators.activitiesEnjoy}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Activities the patient enjoys:',
                }}
            />
            <FieldTextArea
                name="anyAdditionalInfo"
                validate={validators.anyAdditionalInfo}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Additional Information:',
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<ChildTreatmentQuestionnaireFormData, ChildTreatmentQuestionnaireFormProps>({
    form: 'child-treatment-questionnaire-form',
    enableReinitialize: true,
    onSubmit: async (
        data: ChildTreatmentQuestionnaireFormData,
        dispatch: Dispatch<any>,
        props: ChildTreatmentQuestionnaireFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeChildTreatmentQuestionnaire({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<ChildTreatmentQuestionnaireFormData, ChildTreatmentQuestionnaireFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(ChildTreatmentQuestionnaireForm);
